import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Layout } from "../components/layout/Layout";
import { Section, SectionHeader } from "../components/common/Section";
import { FaCogs, FaLaptopCode, FaHandshake, FaShieldAlt, FaArrowRight, FaPlay } from "react-icons/fa";

const Home = () => {
  const features = [
    {
      icon: FaCogs,
      title: "Cabinet Manufacturing",
      description: "Custom-built gaming cabinets with premium materials and precision engineering for lasting durability.",
    },
    {
      icon: FaLaptopCode,
      title: "Software Development",
      description: "Cutting-edge gaming software developed in-house for seamless performance and engaging experiences.",
    },
    {
      icon: FaHandshake,
      title: "Vending & Leasing",
      description: "Flexible vending and leasing options tailored to your business needs and growth objectives.",
    },
    {
      icon: FaShieldAlt,
      title: "Quality Assurance",
      description: "Rigorous testing and quality control ensuring every product meets our premium standards.",
    },
  ];

  return (
    <Layout>
      <Helmet>
        <title>Crown Amusement | Skill Game Cabinets, Fish Games, Online Gaming & Vending Solutions</title>
        <meta name="description" content="Crown Amusement is a leading manufacturer and distributor of premium skill game cabinets, fish games, online skill games, gaming kiosks, and vending solutions. Buy, sell, or lease gaming machines from Banilla, Jenka, and Primero." />
        <link rel="canonical" href="https://crownamusement.com/" />
        <meta property="og:title" content="Crown Amusement | Premium Skill Game Cabinets & Gaming Solutions" />
        <meta property="og:description" content="Industry leaders in manufacturing premium skill game cabinets, fish games, online skill games, and vending solutions." />
        <meta property="og:url" content="https://crownamusement.com/" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/images/hero-background.webp"
            alt="Skill gaming cabinets"
            className="w-full h-full object-cover object-center"
            width="1920"
            height="1080"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-obsidian/90" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-6 tracking-wider uppercase border border-gold-500/20"
            >
              Premium Skill Gaming Solutions
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-heading text-5xl md:text-7xl font-bold text-white tracking-tight leading-tight"
            >
              The Future of{" "}
              <span className="text-gold-gradient">Skill Gaming</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-6 text-lg md:text-xl text-gray-300 leading-relaxed"
            >
              Industry leaders in manufacturing premium skill game cabinets, 
              developing cutting-edge gaming software, and providing flexible 
              vending solutions for entertainment venues worldwide.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-bold rounded-full hover:scale-105 transition-transform shadow-gold-glow"
              >
                Explore Products
                <FaArrowRight />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gold-500/30 text-gold-400 font-semibold rounded-full hover:bg-gold-500/10 transition-colors backdrop-blur-sm"
              >
                <FaPlay className="text-sm" />
                Get Started
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-gold-500/30 flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-3 bg-gold-500 rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <Section background="gradient" id="features">
        <SectionHeader
          label="Why Crown Amusement"
          title="Excellence in Every Detail"
          description="From concept to deployment, we deliver comprehensive gaming solutions that set industry standards."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:border-gold-500/30 transition-all duration-300 group"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-gold-500/10 flex items-center justify-center mb-6 group-hover:bg-gold-500/20 transition-colors">
                <feature.icon className="text-2xl text-gold-500" />
              </div>
              <h3 className="font-heading text-xl text-white mb-3">{feature.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Manufacturing CTA */}
      <Section background="surface">
        <div className="relative rounded-3xl overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1758260759878-f58196990628?crop=entropy&cs=srgb&fm=webp&w=600&h=384&fit=crop&q=50"
            alt="Skill game cabinet manufacturing facility"
            className="w-full h-96 object-cover"
            width="600"
            height="384"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-xl px-8 md:px-16">
              <span className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-4 tracking-wider uppercase border border-gold-500/20">
                Manufacturing
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-white font-semibold">
                Built with Precision, Designed for Excellence
              </h2>
              <p className="mt-4 text-gray-300 leading-relaxed">
                Our state-of-the-art manufacturing facility combines traditional craftsmanship 
                with modern technology to produce gaming cabinets that exceed industry standards.
              </p>
              <Link
                to="/manufacturing"
                data-testid="manufacturing-cta"
                className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-bold rounded-full hover:scale-105 transition-transform"
              >
                Explore Our Manufacturing
                <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* Contact CTA */}
      <Section background="default">
        <div className="text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl text-white font-semibold"
          >
            Ready to Elevate Your Venue?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="mt-6 text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Whether you want to buy, sell, or lease gaming machines, 
            our team is here to help you find the perfect solution.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/contact"
              data-testid="contact-cta"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-bold rounded-full hover:scale-105 transition-transform shadow-gold-glow"
            >
              Contact Us Today
              <FaArrowRight />
            </Link>
            <a
              href="tel:+18482198830"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gold-500/30 text-gold-400 font-semibold rounded-full hover:bg-gold-500/10 transition-colors"
            >
              +1 848-219-8830
            </a>
          </motion.div>
        </div>
      </Section>
    </Layout>
  );
};

export default Home;
